import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  FormControlLabel, Switch, Autocomplete, Alert,
} from '@mui/material';
import { useApp } from '../context/AppContext';

export default function CreateFeaturePage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { flags, createFlag } = useApp();

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [userDisplayMessage, setUserDisplayMessage] = useState('');
  const [masterActive, setMasterActive] = useState(false);
  const [dependencies, setDependencies] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const existingKeys = flags.map(f => f.key);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    setError(null);
    setSaving(true);
    try {
      await createFlag(projectId, { key, name, userDisplayMessage, masterActive, dependencies });
      navigate(`/projects/${projectId}/flags`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create flag');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box maxWidth={600}>
      <Typography variant="h5" fontWeight={700} mb={3}>Create Feature Flag</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Flag Key" value={key}
              onChange={e => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              helperText="Slug identifier (immutable after creation)" margin="normal" required
            />
            <TextField
              fullWidth label="Display Name" value={name}
              onChange={e => setName(e.target.value)}
              margin="normal" required
            />
            <TextField
              fullWidth label="User Display Message" value={userDisplayMessage}
              onChange={e => setUserDisplayMessage(e.target.value)}
              margin="normal" multiline rows={2}
              helperText="Message shown to users when feature is disabled"
            />
            <Autocomplete
              multiple freeSolo
              options={existingKeys}
              value={dependencies}
              onChange={(_, val) => setDependencies(val as string[])}
              renderInput={params => (
                <TextField {...params} label="Dependencies" margin="normal"
                  helperText="All must be enabled for this flag to be active" />
              )}
            />
            <FormControlLabel
              control={<Switch checked={masterActive} onChange={e => setMasterActive(e.target.checked)} />}
              label="Master Active"
              sx={{ mt: 1 }}
            />
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Creating...' : 'Create Flag'}
              </Button>
              <Button onClick={() => navigate(`/projects/${projectId}/flags`)}>Cancel</Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
